/**
 * Mirrors backend App\Support\RequestStatusFlows.
 *
 * Status IDs:
 *  1=new, 2=initial_review, 3=quoting, 4=quoted, 5=final_review,
 *  6=approved, 7=rejected, 8=scheduled, 9=quality_review, 10=completed
 *  11=submitted(arc), 12=under_review(arc), 13=approved_with_conditions(arc), 14=needs_more_info(arc)
 */

// ── Membership lists (all statuses valid for a type) ────────────────────────

export const MAINTENANCE_STATUS_IDS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
export const SIMPLE_STATUS_IDS = [1, 2, 7, 10];
export const ARC_STATUS_IDS = [1, 11, 12, 6, 13, 14, 7];

// ── Transition tables (from → allowed next) ──────────────────────────────────
// Must stay in sync with backend App\Support\RequestStatusFlows.

const MAINTENANCE_TRANSITIONS: Record<number, number[]> = {
  1: [2, 7],        // new → initial_review, rejected
  2: [3, 7],        // initial_review → quoting, rejected
  3: [4, 7],        // quoting → quoted, rejected
  4: [5, 3, 7],     // quoted → final_review | quoting (rework) | rejected
  5: [6, 3, 7],     // final_review → approved | quoting (rework) | rejected
  6: [8, 7],        // approved → scheduled, rejected
  8: [9, 7],        // scheduled → quality_review, rejected
  9: [10, 8, 7],    // quality_review → completed | scheduled (rework) | rejected
  10: [],           // completed — terminal
  7: [],            // rejected — terminal
};

const SIMPLE_TRANSITIONS: Record<number, number[]> = {
  1: [2, 10, 7],    // new → initial_review | completed | rejected
  2: [10, 7],       // initial_review → completed | rejected
  10: [],
  7: [],
};

const ARC_TRANSITIONS: Record<number, number[]> = {
  1: [11, 7],           // new → submitted, rejected
  11: [12, 7],          // submitted → under_review, rejected
  12: [6, 13, 14, 7],   // under_review → approved | approved_with_conditions | needs_more_info | rejected
  14: [12, 7],          // needs_more_info → under_review, rejected
  6: [],                // approved — terminal
  13: [],               // approved_with_conditions — terminal
  7: [],
};

const TRANSITIONS_BY_TYPE: Record<string, Record<number, number[]>> = {
  maintenance: MAINTENANCE_TRANSITIONS,
  general: SIMPLE_TRANSITIONS,
  incident: SIMPLE_TRANSITIONS,
  security: SIMPLE_TRANSITIONS,
  suggestions: SIMPLE_TRANSITIONS,
  arc: ARC_TRANSITIONS,
};

// ── Public helpers ────────────────────────────────────────────────────────────

/** All statuses that belong to a type (used for display/filtering outside a specific state). */
export function getAllowedStatusIds(typeName?: string | null): number[] {
  const transitions = TRANSITIONS_BY_TYPE[typeName?.toLowerCase() ?? ''] ?? SIMPLE_TRANSITIONS;
  return Object.keys(transitions).map(Number);
}

/**
 * Status IDs that may be selected from the current state.
 * Always includes the current status itself so it renders as selected in the UI.
 * Falls back to the full membership list for legacy/unknown current statuses.
 */
export function getAllowedNextStatusIds(
  typeName: string | null | undefined,
  currentStatusId: number | null | undefined
): number[] {
  if (!currentStatusId) return getAllowedStatusIds(typeName);

  const transitions = TRANSITIONS_BY_TYPE[typeName?.toLowerCase() ?? ''] ?? SIMPLE_TRANSITIONS;

  if (!(currentStatusId in transitions)) {
    // Legacy record with an unrecognised status — show the full type membership.
    return getAllowedStatusIds(typeName);
  }

  return [currentStatusId, ...transitions[currentStatusId]];
}

/** Whether a status transition is valid for a type (client-side mirror of the backend guard). */
export function isValidTransition(
  typeName: string | null | undefined,
  fromStatusId: number,
  toStatusId: number
): boolean {
  if (fromStatusId === toStatusId) return true;
  const transitions = TRANSITIONS_BY_TYPE[typeName?.toLowerCase() ?? ''] ?? SIMPLE_TRANSITIONS;
  if (!(fromStatusId in transitions)) return getAllowedStatusIds(typeName).includes(toStatusId);
  return transitions[fromStatusId].includes(toStatusId);
}

/** True when the request type is ARC */
export function isArcRequest(typeName?: string | null): boolean {
  return typeName?.toLowerCase() === 'arc';
}
